import type {
  LeafPrimitive,
  PrinterProfile,
  RegionBorders,
  Rotation,
  Template,
} from '@tsagko/label-contracts'
import type { FlatZone } from './flatten-zones'

export interface ResolvedPrintRotation {
  /** 0 = none, 90 = clockwise, 270 = counter-clockwise (ZPL rotation is CW). */
  deg: 0 | 90 | 270
  direction: 'cw' | 'ccw'
}

/** Pure decision: rotate? which way? Absent printRotation ⇒ auto+cw. */
export function resolvePrintRotation(
  template: Template,
  profile: PrinterProfile
): ResolvedPrintRotation {
  const pr = template.printRotation ?? { mode: 'auto' as const, direction: 'cw' as const }
  const direction = pr.direction
  const degForDir: 90 | 270 = direction === 'cw' ? 90 : 270
  if (pr.mode === 'off') return { deg: 0, direction }
  if (pr.mode === 'on') return { deg: degForDir, direction }
  const requestedWidthDots = Math.round((template.widthMm * profile.dpi) / 25.4)
  const acrossHeadIfRotated = Math.round((template.heightMm * profile.dpi) / 25.4)
  const tooWide = requestedWidthDots > profile.printWidthDots
  const heightFits = acrossHeadIfRotated <= profile.printWidthDots
  return { deg: tooWide && heightFits ? degForDir : 0, direction }
}

function addRotation(r: Rotation | undefined, deg: 90 | 270): Rotation {
  return (((r ?? 0) + deg) % 360) as Rotation
}

/**
 * Permute per-edge borders to follow the box under rotation, omitting absent
 * edges (so the result has no explicit-undefined keys).
 *   CW (90):  top→right, right→bottom, bottom→left, left→top
 *   CCW (270): top→left, left→bottom, bottom→right, right→top
 */
function rotateBorders(b: RegionBorders | undefined, deg: 90 | 270): RegionBorders | undefined {
  if (!b) return undefined
  return deg === 90
    ? {
        ...(b.left && { top: b.left }),
        ...(b.top && { right: b.top }),
        ...(b.right && { bottom: b.right }),
        ...(b.bottom && { left: b.bottom }),
      }
    : {
        ...(b.right && { top: b.right }),
        ...(b.top && { left: b.top }),
        ...(b.left && { bottom: b.left }),
        ...(b.bottom && { right: b.bottom }),
      }
}

function rotateComponent(c: LeafPrimitive, deg: 90 | 270): LeafPrimitive {
  // Only these carry a renderer-honored `rotation`. Barcodes (symbol) honor it
  // after Task #14. Image rotation is realized by rotating the raster (later
  // task) — ^GFA can't rotate via a char. Separator rotates purely from the box
  // w/h swap (its `orientation` field is dead in the render path).
  // localized-rich-text has no rotation field (known limitation).
  switch (c.type) {
    case 'text':
    case 'symbol':
    case 'localized-text':
      return { ...c, rotation: addRotation(c.rotation, deg) }
    default:
      return c
  }
}

/**
 * Rotate flattened zones from design space (W×H) to physical (H×W).
 *   CW  (90):  x' = H-(y+h), y' = x,       w' = h, h' = w
 *   CCW (270): x' = y,       y' = W-(x+w), w' = h, h' = w
 */
export function rotateFlatZones(
  zones: FlatZone[],
  deg: 90 | 270,
  designWidthMm: number,
  designHeightMm: number
): FlatZone[] {
  const W = designWidthMm
  const H = designHeightMm
  return zones.map(z => {
    const box =
      deg === 90
        ? { xMm: H - (z.yMm + z.heightMm), yMm: z.xMm, widthMm: z.heightMm, heightMm: z.widthMm }
        : { xMm: z.yMm, yMm: W - (z.xMm + z.widthMm), widthMm: z.heightMm, heightMm: z.widthMm }
    return {
      ...z,
      ...box,
      component: rotateComponent(z.component, deg),
      borders: rotateBorders(z.borders, deg),
    }
  })
}
